import React, { useEffect } from 'react';
import { HashRouter, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { ToastProvider } from "components/ui/Toast/ToastProvider";
import { API_BASE_URL } from "config/apiConfig";
import { clearAllAuthStorage } from "utils/tokenManager";
import MainLayout from 'layouts/MainLayout';

// 토큰 확인 유틸
import { getTokenStatus } from 'utils/tokenManager';
import { BranchProvider } from "contexts/BranchContext";

// CSS imports [제거 금지]
import 'styles/index.css';

// Router imports [제거 금지]
import PolicyWebPage from "pages/PolicyPage/PolicyWebPage";
import PolicyAppPage from "pages/PolicyPage/PolicyAppPage";
import Login from "pages/Auth/Login/Login";
import FindPassword from "pages/Auth/Login/components/FindPassword";
import Agreement from "pages/Auth/Agreement/Agreement";
import Verification from "pages/Auth/Verification/Verification";
import BranchSelect from "pages/Auth/BranchSelect/BranchSelect";
import SignUp from "pages/Auth/SignUp/SignUp";
import Home from "pages/Home/Home";
import ReviewWritePage from "pages/Review/ReviewWritePage";
import Boost from "pages/Boost/Boost";
import BoostDetail from "pages/Boost/components/BoostDetail";
import Ipt from "pages/Ipt/Ipt";
import IptDetail from "pages/Ipt/components/IptDetail";
import Lylo from "pages/Lylo/Lylo";
import LyloDetail from "pages/Lylo/components/LyloDetail";
import MyReservations from "pages/MyPage/components/MyReservations";
import Pt from "pages/Pt/Pt";
import PtDetail from "pages/Pt/components/PtDetail";
import PtInquiry from "pages/Pt/components/PtInquiry";
import MyPage from "pages/MyPage/MyPage";
import CardRegist from "pages/MyPage/components/CardRegistration";
import MyProfileEdit from "pages/MyPage/components/MyProfileEdit";
import MyReview from "pages/Review/MyReview";
import Product from "pages/Product/Product"
import PaymentHistory from "pages/MyPage/components/PaymentHistory";
import BranchList from "pages/Branch/BranchList";
import BranchInfo from "pages/Branch/component/BranchInfo";
import PaymentSuccess from "pages/Product/components/PaymentSuccess";
import PaymentCancel from "pages/Product/components/PaymentCancel";
import PaymentFail from "pages/Product/components/PaymentFail";
import AttendanceHistory from "pages/MyPage/components/AttendanceHistory";
import SettingsPage from "pages/Settings/Settings";

function AppContent() {
    const navigate = useNavigate();
    const location = useLocation(); // 현재 경로 확인용

    // 웹뷰 캐시 무력화 (버전 체크 및 강제 리로드)
    useEffect(() => {
        const checkAppVersion = async () => {
            try {
                const response = await fetch(`/version.json?t=${Date.now()}`);
                if (!response.ok) return;
                const data = await response.json();
                const latestVersion = data.version;
                const localVersion = localStorage.getItem('appVersion');

                if (latestVersion && latestVersion !== localVersion) {
                    localStorage.setItem('appVersion', latestVersion);
                    // 최초 접속(localVersion이 없는 상태)이 아닐 때만 강제 리로드
                    if (localVersion) {
                        // 네이티브(Flutter) 웹뷰 캐시 초기화 요청. 서버가 index.html에
                        // Cache-Control 헤더를 안 보내는 환경이라, 앱을 새로 켜지 않고
                        // 웹뷰가 이미 떠 있는 상태에서 버전 변경을 감지했을 때도
                        // 캐시된 옛 번들을 그대로 다시 불러오지 않도록 방지한다.
                        // 리로드 전에 캐시 삭제가 끝나도록 반드시 await한다.
                        if (window.flutter_inappwebview) {
                            try {
                                await window.flutter_inappwebview.callHandler('ClearWebViewCache');
                            } catch (e) {
                                console.error("웹뷰 캐시 초기화 요청 실패:", e);
                            }
                        }
                        const baseUrl = window.location.origin + window.location.pathname;
                        const hash = window.location.hash;
                        window.location.replace(`${baseUrl}?v=${latestVersion}${hash}`);
                    }
                }
            } catch (e) {
                console.error("앱 버전 확인 실패:", e);
            }
        };
        checkAppVersion();
    }, []);

    // 로그인 없이 접근 가능한 경로 목록 (Public Route)
    const publicPaths = [
        '/', '/agreement', '/verification', '/signup/branch', '/signup', '/findpw', '/policy/web',
    ];

    // 전역 인증 체크 로직
    useEffect(() => {
        const { token } = getTokenStatus();
        const currentPath = location.pathname;

        // 현재 경로가 Public 경로에 포함되지 않는데, 토큰이 없다면 -> 로그인으로 튕겨냄
        if (!publicPaths.includes(currentPath) && !token) {
            navigate('/', { replace: true });
        }
    }, [location.pathname, navigate]);

    // 앱 실행(로드) 시 서버와 통신하여 토큰/계정 무결성 체크
    useEffect(() => {
        const checkUserIntegrity = async () => {
            const { token } = getTokenStatus();

            // 토큰이 없으면 1번 로직이 처리하므로 패스, Public 경로는 체크 불필요
            if (!token || publicPaths.includes(location.pathname)) return;

            try {
                // userInfo API를 호출하여 토큰이 유효한지, 계정이 살아있는지 확인
                const response = await fetch(`${API_BASE_URL}/checkUser`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                const resp = await response.json();
                const resdata = resp.data;

                // 401 Unauthorized 등 에러 발생 시
                if (!(!response.ok && (resdata.code === 200))) {
                    return;
                }
                throw new Error("Invalid Token or Server Error");
                navigate('/', { replace: true });

                // 성공 시 아무 동작 안 함 (정상 진행)

            } catch (error) {
                console.error("무결성 체크 실패:", error);

                // 문제가 있다면 로그아웃 처리 후 로그인 페이지로 강제 이동
                clearAllAuthStorage();
                alert("로그인 정보가 만료되었거나 유효하지 않습니다.\n다시 로그인해주세요.");
                navigate('/', { replace: true });
            }
        };

        checkUserIntegrity();
        // 의존성 배열을 비워두거나 location.pathname을 넣어서 페이지 이동시마다 체크
        // 여기서는 앱 로드 시(혹은 새로고침 시) 한 번 체크하도록 설정
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 플러터와의 통신 브릿지 설정
    useEffect(() => {
        // 플러터가 호출할 함수 정의
        window.setDeviceInfo = (jsonString) => {
            try {
                const deviceInfo = JSON.parse(jsonString);

                // 세션 스토리지에 저장 (Login.jsx에서 쓰기 위해)
                sessionStorage.setItem('deviceInfo', JSON.stringify(deviceInfo));
            } catch (e) {
                console.error("디바이스 정보 파싱 에러:", e);
            }
        };

        // 혹시 플러터가 더 빨리 데이터를 넣었을 수도 있으니 체크
        const backupInfo = sessionStorage.getItem('deviceInfo');
        if (backupInfo) {
        }

        // 클린업 (선택사항)
        return () => {
            delete window.setDeviceInfo;
        };
    }, []);

    useEffect(() => {
        // [Step 4] 결제 결과 수신 리스너 등록
        window.onPaymentResult = (status, url) => {
            if (status === 'success') {
                if (url) {
                    const urlObj = new URL(url);
                    navigate(`/payment/success${urlObj.search}`);
                } else {
                    navigate('/payment/success');
                }
            } else {
                // 취소 또는 실패
                navigate('/payment/cancel');
            }
        };
        // Cleanup
        return () => {
            delete window.onPaymentResult;
        };
    }, [navigate]);

    return (
        <Routes>
            {/* 로그인/회원가입 */}
            <Route path="/" element={<Login />} /> {/* 로그인 */}
            <Route path="/agreement" element={<Agreement />} /> {/* 정보동의 */}
            <Route path="/verification" element={<Verification />} /> {/* 휴대폰 번호 인증 */}
            <Route path="/signup/branch" element={<BranchSelect />} /> {/* 회원가입 - 지점 선택 */}
            <Route path="/signup" element={<SignUp />} /> {/* 회원가입 */}
            <Route path="/findpw" element={<FindPassword />} /> {/* 비밀번호 찾기 */}

            {/* Protected Routes (위의 useEffect에 의해 토큰 없으면 접근 불가) */}
            <Route element={<MainLayout />}>
                <Route path="/home" element={<Home />} /> {/* 홈 */}
                <Route path="/sb" element={<Boost />} /> {/* 부스트 강사 목록 */}
                <Route path="/ipt" element={<Ipt />} /> {/* IPT 강사 목록 */}
                <Route path="/lylo" element={<Lylo />} /> {/* LYLO 강사 목록 */}
                <Route path="/pt" element={<Pt />} /> {/* PT 강사 목록*/}
                <Route path="/my" element={<MyPage />} /> {/* 마이페이지 */}
            </Route>

            {/* 약관 등 기타 */}
            <Route path="/policy/web" element={<PolicyWebPage />} /> {/* 웹 약관 확인 페이지 */}
            <Route path="/policy/app" element={<PolicyAppPage />} /> {/* 모바일 약관 확인 페이지 */}

            {/* 상세 페이지 / 기능 페이지 */}
            <Route path="/setting" element={<SettingsPage />} /> {/* 설정 페이지 */}
            <Route path="/review/write" element={<ReviewWritePage />} /> {/* 리뷰 작성 페이지 */}
            <Route path="/sb/detail" element={<BoostDetail />} /> {/* 부스트 강사 상세 */}
            <Route path="/sb/reserve" element={<MyReservations />} /> {/* 부스트 예약 목록 */}
            <Route path="/ipt/detail" element={<IptDetail />} /> {/* IPT 강사 상세 */}
            <Route path="/lylo/detail" element={<LyloDetail />} /> {/* LYLO 강사 상세 */}
            <Route path="/pt/detail" element={<PtDetail />} /> {/* PT 강사 상세 */}
            <Route path="/pt/inquiry" element={<PtInquiry />} /> {/* PT 문의 페이지 */}
            <Route path="/my/card" element={<CardRegist />} /> {/* 등록 카드 관리 (삭제 예정)*/}
            <Route path="/my/profile" element={<MyProfileEdit />} /> {/* 회원정보 */}
            <Route path="/my/reviews" element={<MyReview />} /> {/* 내 리뷰 */}
            <Route path="/my/attendance" element={<AttendanceHistory />} /> {/* 출석 내역 */}
            <Route path="/prod/:brKey" element={<Product />} /> {/* 지점 상품 */}
            <Route path="/payment" element={<PaymentHistory />} /> {/* 결제 페이지 */}
            <Route path="/branch" element={<BranchList />} /> {/* 지점 목록 */}
            <Route path="/brinfo/:brKey" element={<BranchInfo />} /> {/* 지점 정보 */}
            <Route path="/payment/success" element={<PaymentSuccess />} /> {/* 결제 성공 */}
            <Route path="/payment/cancel" element={<PaymentCancel />} /> {/* 결제 취소 */}
            <Route path="/payment/fail" element={<PaymentFail />} /> {/* 결제 실패 */}
        </Routes>
    );
}

function App() {
    return (
        <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <BranchProvider>
                <ToastProvider>
                    {/* Routes를 감싸는 AppContent 사용 */}
                    <AppContent />
                </ToastProvider>
            </BranchProvider>
        </HashRouter>
    );
}

export default App;